import type { ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import {
  DashboardDetailsPageSkeleton,
  DashboardDetailsTab,
  DashboardDetailsTabList,
  DashboardDetailsHeader,
  dashboardPageTitleClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, Tabs, TabsContent } from '@shigo-ui/ui';
import { ReturnBackLink } from '../../../components/navigation/ReturnBackLink';
import { returnFallbackFromPath } from '../../../lib/returnNavigation';
import { dashboardT } from '../../../lib/messages';
import type { HierarchyLink } from '../hierarchyLinks';

enum DetailsTab {
  Overview = 'overview',
}

export function CampaignDetailsLayout({
  name,
  loading,
  error,
  back,
  onEdit,
  backLabel = dashboardT('common.back'),
  loadingLabel = dashboardT('campaignHierarchy.loading'),
  errorLabel = dashboardT('campaignHierarchy.error'),
  children,
}: {
  name?: string | undefined;
  loading: boolean;
  error: boolean;
  back: HierarchyLink;
  onEdit?: (() => void) | undefined;
  backLabel?: string;
  loadingLabel?: string;
  errorLabel?: string;
  children: ReactNode;
}) {
  if (loading)
    return <DashboardDetailsPageSkeleton label={loadingLabel} showHeaderAction={Boolean(onEdit)} />;
  return (
    <main className="min-w-0 overflow-x-clip">
      <Tabs value={DetailsTab.Overview}>
        <div className="border-border bg-card/60 border-b">
          <section className="page-gutter mx-auto flex w-full max-w-[1920px] flex-col gap-4 pt-5">
            <DashboardDetailsHeader
              back={<ReturnBackLink fallback={returnFallbackFromPath(back)} label={backLabel} />}
              title={
                <h1 className={dashboardPageTitleClassName('min-w-0 truncate')}>
                  {error ? errorLabel : name}
                </h1>
              }
              primaryAction={
                !error && onEdit ? (
                  <Button variant="ghost" size="sm" onClick={onEdit}>
                    <Pencil className="size-4" aria-hidden="true" />
                    {dashboardT('nav.action.edit')}
                  </Button>
                ) : undefined
              }
            />
            <DashboardDetailsTabList>
              <DashboardDetailsTab value={DetailsTab.Overview}>
                {dashboardT('campaigns.overview')}
              </DashboardDetailsTab>
            </DashboardDetailsTabList>
          </section>
        </div>
        {!error ? (
          <section className="page-gutter mx-auto w-full max-w-[1920px] min-w-0 py-6">
            <TabsContent value={DetailsTab.Overview} className="mt-0 min-w-0">
              {children}
            </TabsContent>
          </section>
        ) : null}
      </Tabs>
    </main>
  );
}
